/**
 * Admin Feedback Management Page
 *
 * Allows admins to view, manage, and respond to user feedback.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  BugReport as BugIcon,
  Lightbulb as FeatureIcon,
  Build as ImprovementIcon,
  HelpOutline as QuestionIcon,
  Reply as ReplyIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { feedbackApi, Feedback, FeedbackAdminUpdate } from '@/services/api';
import { useAuth } from '@/context/AuthContext';

const feedbackTypeOptions = [
  { value: 'bug_report', label: 'Bug Report', icon: <BugIcon /> },
  { value: 'feature_request', label: 'Feature Request', icon: <FeatureIcon /> },
  { value: 'improvement', label: 'Improvement', icon: <ImprovementIcon /> },
  { value: 'question', label: 'Question', icon: <QuestionIcon /> },
  { value: 'other', label: 'Other', icon: null },
];

const statusOptions = [
  { value: 'new', label: 'New' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'declined', label: 'Declined' },
];

const priorityOptions = [
  { value: 'low', label: 'Low', color: 'default' as const },
  { value: 'medium', label: 'Medium', color: 'info' as const },
  { value: 'high', label: 'High', color: 'warning' as const },
  { value: 'critical', label: 'Critical', color: 'error' as const },
];

const statusColors: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  new: 'default',
  under_review: 'info',
  planned: 'info',
  in_progress: 'warning',
  resolved: 'success',
  declined: 'error',
};

export default function AdminFeedbackPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminResponse, setAdminResponse] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [newPriority, setNewPriority] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch feedback with filters
  const { data: feedback, isLoading } = useQuery({
    queryKey: ['admin-feedback', statusFilter, typeFilter],
    queryFn: () =>
      feedbackApi
        .list({
          status: statusFilter || undefined,
          type: typeFilter || undefined,
          page_size: 100,
        })
        .then((res) => res.data),
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['feedback-stats'],
    queryFn: () => feedbackApi.getStats().then((res) => res.data),
  });

  // Admin update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FeedbackAdminUpdate }) =>
      feedbackApi.adminUpdate(id, data, user?.email || ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-feedback'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-stats'] });
      setDialogOpen(false);
      setSelectedFeedback(null);
      setAdminResponse('');
      setNewStatus('');
      setNewPriority('');
      setSnackbar({ open: true, message: 'Feedback updated successfully!', severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Failed to update: ${error.message}`, severity: 'error' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => feedbackApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-feedback'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-stats'] });
      setSnackbar({ open: true, message: 'Feedback deleted', severity: 'success' });
    },
  });

  const handleOpenDialog = (item: Feedback) => {
    setSelectedFeedback(item);
    setAdminResponse(item.admin_response || '');
    setNewStatus(item.status);
    setNewPriority(item.priority);
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!selectedFeedback) return;

    const update: FeedbackAdminUpdate = {};
    if (newStatus !== selectedFeedback.status) update.status = newStatus;
    if (newPriority !== selectedFeedback.priority) update.priority = newPriority;
    if (adminResponse !== (selectedFeedback.admin_response || '')) update.admin_response = adminResponse;

    if (Object.keys(update).length === 0) {
      setDialogOpen(false);
      return;
    }

    updateMutation.mutate({ id: selectedFeedback.id, data: update });
  };

  return (
    <Box>
      {/* Stats */}
      <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, mb: 2, display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Total</Typography>
          <Typography variant="subtitle1" fontWeight={700}>{stats?.total || 0}</Typography>
        </Box>
        {stats?.by_status && Object.entries(stats.by_status).map(([key, value]) => (
          <Box key={key} sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{key.replace('_', ' ')}</Typography>
            <Typography variant="subtitle1" fontWeight={700} color={statusColors[key] === 'success' ? 'success.main' : statusColors[key] === 'error' ? 'error.main' : statusColors[key] === 'warning' ? 'warning.main' : statusColors[key] === 'info' ? 'info.main' : 'text.primary'}>{value as number}</Typography>
          </Box>
        ))}
      </Paper>

      {/* Filters */}
      <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {statusOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Type Filter</InputLabel>
              <Select
                value={typeFilter}
                label="Type Filter"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {feedbackTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              onClick={() => {
                setStatusFilter('');
                setTypeFilter('');
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Feedback Table */}
      {isLoading ? (
        <Skeleton variant="rectangular" height={400} />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Submitted By</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Votes</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feedback?.items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Chip
                      label={item.feedback_type.replace('_', ' ')}
                      size="small"
                      icon={feedbackTypeOptions.find((t) => t.value === item.feedback_type)?.icon || undefined}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {item.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.submitted_by_name || item.submitted_by_email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.priority}
                      size="small"
                      color={priorityOptions.find((p) => p.value === item.priority)?.color || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.status.replace('_', ' ')}
                      size="small"
                      color={statusColors[item.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>{item.vote_count}</TableCell>
                  <TableCell>{new Date(item.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Tooltip title="Respond / Update">
                      <IconButton size="small" onClick={() => handleOpenDialog(item)}>
                        <ReplyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          if (confirm('Are you sure you want to delete this feedback?')) {
                            deleteMutation.mutate(item.id);
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {feedback?.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No feedback found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={feedback?.items.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      )}

      {/* Response Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Respond to Feedback</DialogTitle>
        <DialogContent>
          {selectedFeedback && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {selectedFeedback.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                By {selectedFeedback.submitted_by_name || selectedFeedback.submitted_by_email} on{' '}
                {new Date(selectedFeedback.created_at).toLocaleString()}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedFeedback.description}
                </Typography>
              </Paper>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select value={newStatus} label="Status" onChange={(e) => setNewStatus(e.target.value)}>
                      {statusOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select value={newPriority} label="Priority" onChange={(e) => setNewPriority(e.target.value)}>
                      {priorityOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Admin Response"
                    fullWidth
                    multiline
                    rows={4}
                    value={adminResponse}
                    onChange={(e) => setAdminResponse(e.target.value)}
                    placeholder="Provide a response to the user..."
                    helperText="This response will be visible to the user who submitted the feedback"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
